import React, { useMemo } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';
import Breadcrumbs from 'components/Common/Breadcrumb';
import { DataTable } from 'components/DataTable';
import { DataTableColumn, TableQueryParams, PaginatedResponse } from 'types/table.types';
import { SHOP_PATHS } from 'routes/routesConstants';
import { useShop } from 'context/shopContext';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { DataTableApiService } from 'services/api/tableApi';
import { formatFullDate, formatRelativeDate } from 'utils/dateFormatter';

interface SellInvoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  customerName: string;
  totalAmount: number;
  isActive: boolean;
  remarks?: string;
  createdAt: string;
  updatedAt?: string;
}

const SellInvoiceList: React.FC = () => {
  const { shop } = useShop();
  const { id: shopId } = shop!;
  const navigate = useNavigate();

  /**
   * Define columns for the data table
   */
  const columns = useMemo<DataTableColumn<SellInvoice>[]>(
    () => [
      {
        accessorKey: 'invoiceNumber',
        id: 'invoiceNumber',
        header: 'Invoice Number',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<SellInvoice, unknown>) => (
          <Link
            to={SHOP_PATHS.MEDICINE_SELL_INVOICE_DETAIL.replace(':shopId', shopId || '').replace(
              ':invoiceId',
              info.row.original.id,
            )}
            className="text-dark fw-bold"
          >
            {info.getValue() as string}
          </Link>
        ),
      },
      {
        accessorKey: 'invoiceDate',
        id: 'invoiceDate',
        header: 'Date',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<SellInvoice, unknown>) => {
          return formatFullDate(info.getValue() as string, false);
        },
      },
      {
        accessorKey: 'customerName',
        id: 'customerName',
        header: 'Customer',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<SellInvoice, unknown>) => {
          const customerName = (info.getValue() as string) || 'Walk-in Customer';
          return <span className="fw-medium">{customerName}</span>;
        },
      },
      {
        accessorKey: 'totalAmount',
        id: 'totalAmount',
        header: 'Total Amount',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<SellInvoice, unknown>) => {
          const amount = info.getValue() as number;
          return <span className="fw-bold text-success">${amount?.toFixed(2)}</span>;
        },
      },
      // {
      //   accessorKey: 'status',
      //   id: 'status',
      //   header: 'Status',
      //   enableSorting: true,
      //   enableFiltering: true,
      //   filterType: 'checkbox',
      //   cell: (info: CellContext<SellInvoice, unknown>) => {
      //     const status = info.getValue() as boolean;
      //     return <span className={`badge ${status ? 'bg-success' : 'bg-danger'}`}>{status}</span>;
      //   },
      // },
      {
        accessorKey: 'remarks',
        id: 'remarks',
        header: 'Remarks',
        enableSorting: false,
        enableFiltering: true,
        cell: (info: CellContext<SellInvoice, unknown>) => {
          const remarks = (info.getValue() as string) ?? '-';
          return (
            <div
              className="remarks-text"
              style={{
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {remarks}
            </div>
          );
        },
      },
      {
        accessorKey: 'updatedAt',
        id: 'updatedAt',
        header: 'Last Updated',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<SellInvoice, unknown>) => {
          const row = info.row.original;
          const dateToUse = row.updatedAt || row.createdAt;
          const { text, isStale } = formatRelativeDate(dateToUse);
          return <span className={`small ${isStale ? 'text-danger' : 'text-muted'}`}>{text}</span>;
        },
      },
      {
        accessorKey: 'actions',
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        enableFiltering: false,
        cell: (info: CellContext<SellInvoice, unknown>) => {
          const row = info.row.original;
          return (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => {
                  const path = SHOP_PATHS.MEDICINE_SELL_INVOICES_EDIT.replace(
                    ':shopId',
                    shopId || '',
                  ).replace(':invoiceId', row.id);
                  navigate(path);
                }}
              >
                <i className="ri-edit-line"></i> Edit
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => {
                  if (window.confirm(`Delete invoice "${row.invoiceNumber}"?`)) {
                    console.log('Delete invoice:', row.id);
                    // TODO: Implement delete
                  }
                }}
              >
                <i className="ri-delete-bin-line"></i> Delete
              </button>
            </div>
          );
        },
      },
    ],
    [shopId, navigate],
  );

  /**
   * Fetch handler for server-side data
   */
  const handleFetchData = async (
    params: TableQueryParams,
  ): Promise<PaginatedResponse<SellInvoice>> => {
    // Add shopId to filter params
    const filterParams = {
      ...params,
      filters: {
        ...params.filters,
        shopId,
      },
    };
    return DataTableApiService.fetchPaginatedData<SellInvoice>(
      API_ENDPOINTS.TENANT.SELLS.PAGINATED.replace(':shopId', shopId || ''),
      filterParams,
    );
  };

  /**
   * Add invoice handler
   */
  const handleAddInvoice = () => {
    navigate(SHOP_PATHS.MEDICINE_SELL_ENTRY.replace(':shopId', shopId || ''));
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg="12">
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Sell Invoices</h5>
              <button className="btn btn-success" onClick={handleAddInvoice}>
                <i className="ri-add-line"></i> Add Sell Invoice
              </button>
            </div>

            <DataTable
              columns={columns}
              queryKey={API_ENDPOINTS.TENANT.SELLS.PAGINATED.replace(':shopId', shopId || '')}
              onFetchData={handleFetchData}
              enableSearch={true}
              enableFiltering={true}
              enablePagination={true}
              enableSorting={true}
              enableColumnVisibility={true}
              enableColumnReorder={false}
              enableRowSelection={false}
              enableBulkActions={false}
              enableExport={true}
              pageSizeOptions={[10, 20, 50, 100]}
              defaultPageSize={10}
              columnVisibilityStorageKey="sell_invoices_visibility"
              columnOrderStorageKey="sell_invoices_order"
              title=""
              emptyMessage="No sell invoices found. Create one to get started."
              striped={true}
              hover={true}
              bordered={true}
              responsive={true}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SellInvoiceList;
